import { FunctionComponent, useMemo } from 'react';
import { Room, RoomUnit } from '@spike/room-model';
import styles from './Units.module.scss';
import { Button } from './Button';
import isEmpty from 'lodash/isEmpty';
import UnitNames from './UnitNames';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { CounterField } from 'components/UI/V3/CounterField/CounterField';
import { v4 as uuid } from 'uuid';

interface UnitsProps {
    room: Room;
    isEdit: boolean;
    loading?: boolean;
    className?: string;
    onChange?: (room: Room) => void;
    onNext?: () => void;
}

export const Units: FunctionComponent<UnitsProps> = props => {
    const activeUnits = useMemo(
        () =>
            props.room.units
                .filter(unit => !unit.destroy)
                .sort((unit, otherUnit) => (unit.id ?? 0) - (otherUnit.id ?? 0)),
        [props.room.units]
    );

    const disabledNext = activeUnits.some(unit => isEmpty(unit.name?.trim()));

    const add = () => {
        const lastRoomNumber = props.room.units
            .map(unit => {
                const match = unit.name.trim().match(/^Room (\d+)$/);
                return match ? parseInt(match[1]) : null;
            })
            .filter(number => number !== null)
            .reduce((max, number) => Math.max(max, number), 1);

        props.onChange?.({
            ...props.room,
            units: [...props.room.units, { uuid: uuid(), name: `Room ${lastRoomNumber + 1}`, active: true }]
        });
    };

    const removeLast = () => {
        if (activeUnits.length > 1) {
            const lastActiveUnit = activeUnits.at(-1);
            if (lastActiveUnit && lastActiveUnit.id) {
                props.onChange?.({
                    ...props.room,
                    units: props.room.units.map(unit =>
                        unit.uuid === lastActiveUnit.uuid ? { ...unit, destroy: true } : unit
                    )
                });
            } else if (lastActiveUnit && !lastActiveUnit.id) {
                props.onChange?.({
                    ...props.room,
                    units: props.room.units.filter(unit => unit.uuid !== lastActiveUnit.uuid)
                });
            }
        }
    };

    const changeQuantityHandler = (value: number) => {
        if (value > activeUnits.length) {
            add();
        } else if (value < activeUnits.length) {
            removeLast();
        }
    };

    const changeHandler = (units: Array<RoomUnit>) => {
        props.onChange?.({ ...props.room, units });
    };

    return (
        <div className={styles.container}>
            <Label>Quantity</Label>
            <CounterField
                value={activeUnits.length}
                minLimit={1}
                hasBorder={true}
                onChange={changeQuantityHandler}
            />
            <UnitNames
                isEdit={props.isEdit}
                units={activeUnits}
                onChange={changeHandler}
            />
            <Button
                label="Next"
                saving={props.loading ?? false}
                disabled={disabledNext}
                onClick={props.onNext}
            />
        </div>
    );
};

export default Units;
